import { Controller } from "@hotwired/stimulus"
import { formatIsoToDisplay, formatIsoToForm, parseFormDateToIso } from "../../util/date"
import { escapeHtml } from "../../util/common"

export default class extends Controller {
  static targets = [
    "input",
    "thisWeekButton",
    "periodButton",
    "chevron",
    "popover",
    "periodsContainer",
    "summary",
    "count",
    "clearButton"
  ]

  static values = {
    filterKey: String,
    tree: Array
  }

  connect() {
    this.selectedIndices = new Set()
    this.flatWeeks = []
    this.monthRanges = []
    this.yearRanges = []
    this.isThisWeekMode = false

    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleFocusOutside = this.handleFocusOutside.bind(this)
    this.handleFilterClear = this.handleFilterClear.bind(this)

    document.addEventListener("click", this.handleClickOutside)
    document.addEventListener("focusin", this.handleFocusOutside)
    this.inputTarget.addEventListener("filter:clear", this.handleFilterClear)

    this.buildFlatIndex()
    this.renderTree()
    this.updateClearButtonVisibility()

    if (this.inputTarget.value && !this.isCurrentWeekValue(this.inputTarget.value)) {
      this.restoreSelectionFromInput()
      this.updateButtonStates(false)
    } else {
      this.selectThisWeek()
    }

    setTimeout(() => {
      this.dispatchInitializedEvent()
    }, 0)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
    document.removeEventListener("focusin", this.handleFocusOutside)
    this.inputTarget.removeEventListener("filter:clear", this.handleFilterClear)
  }

  onToggle(event) {
    event.stopPropagation()
    this.popoverTarget.classList.toggle("hidden")
    this.toggleChevronRotation(this.chevronTarget)
  }

  close() {
    this.popoverTarget.classList.add("hidden")
    this.resetChevronRotation(this.chevronTarget)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  handleFocusOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  buildFlatIndex() {
    this.flatWeeks = []
    this.monthRanges = []
    this.yearRanges = []

    let weekIndex = 0

    this.treeValue.forEach(yearData => {
      const yearStart = weekIndex

      yearData.months.forEach(monthData => {
        const monthStart = weekIndex

        monthData.weeks.forEach(week => {
          this.flatWeeks.push(week)
          weekIndex++
        })

        this.monthRanges.push({ start: monthStart, end: weekIndex - 1 })
      })

      this.yearRanges.push({ start: yearStart, end: weekIndex - 1 })
    })
  }

  renderTree() {
    let weekIndex = 0
    let monthIndex = 0
    let yearIndex = 0

    const html = this.treeValue.map(yearData => {
      const currentYearIndex = yearIndex++
      const monthsHtml = yearData.months.map(monthData => {
        const currentMonthIndex = monthIndex++
        const weeksHtml = monthData.weeks.map(week => {
          return this.renderWeekRow(week, weekIndex++)
        }).join("")

        return this.renderMonthGroup(monthData, currentMonthIndex, weeksHtml)
      }).join("")

      return this.renderYearGroup(yearData, currentYearIndex, monthsHtml, currentYearIndex === 0)
    }).join("")

    this.periodsContainerTarget.innerHTML = html
  }

  renderYearGroup(yearData, yearIndex, monthsHtml, expanded) {
    const range = this.yearRanges[yearIndex]
    const checked = this.isRangeFullySelected(range) ? "checked" : ""

    return `
      <div class="period-filter-year" data-year-index="${yearIndex}">
        <div class="period-filter-year-header">
          <label class="period-filter-year-label">
            <input type="checkbox"
                   class="period-filter-checkbox period-filter-year-checkbox"
                   data-year-index="${yearIndex}"
                   data-action="change->period-filter#onYearToggle"
                   ${checked}>
            <span class="period-filter-year-name">${escapeHtml(String(yearData.year))}</span>
          </label>
          <div class="flex items-center gap-8">
            <span class="period-filter-group-count">${escapeHtml(yearData.count)}</span>
            <button type="button"
                    class="period-filter-year-toggle"
                    data-year-index="${yearIndex}"
                    data-action="click->period-filter#onYearCollapseToggle">
              <i class="fa-solid fa-chevron-down period-filter-year-chevron" style="transform: rotate(${expanded ? "180" : "0"}deg)"></i>
            </button>
          </div>
        </div>
        <div class="period-filter-year-content period-filter-collapsible ${expanded ? "" : "period-filter-collapsible--collapsed"}" data-year-content="${yearIndex}">
          ${monthsHtml}
        </div>
      </div>
    `
  }

  renderMonthGroup(monthData, monthIndex, weeksHtml) {
    const range = this.monthRanges[monthIndex]
    const checked = this.isRangeFullySelected(range) ? "checked" : ""

    return `
      <div class="period-filter-month" data-month-index="${monthIndex}">
        <div class="period-filter-month-header">
          <label class="period-filter-month-label">
            <input type="checkbox"
                   class="period-filter-checkbox period-filter-month-checkbox"
                   data-month-index="${monthIndex}"
                   data-action="change->period-filter#onMonthToggle"
                   ${checked}>
            <span class="period-filter-month-name">${escapeHtml(monthData.name)}</span>
          </label>
          <div class="flex items-center gap-8">
            <span class="period-filter-group-count">${escapeHtml(monthData.count)}</span>
            <button type="button"
                    class="period-filter-month-toggle"
                    data-month-index="${monthIndex}"
                    data-action="click->period-filter#onMonthCollapseToggle">
              <i class="fa-solid fa-chevron-down period-filter-month-chevron" style="transform: rotate(180deg)"></i>
            </button>
          </div>
        </div>
        <div class="period-filter-month-content period-filter-collapsible" data-month-content="${monthIndex}">
          ${weeksHtml}
        </div>
      </div>
    `
  }

  renderWeekRow(week, index) {
    const checked = this.selectedIndices.has(index) ? "checked" : ""
    const disabled = this.isDisabled(index) ? "disabled" : ""

    return `
      <label class="period-filter-row ${checked ? "period-filter-row--selected" : ""} ${disabled ? "period-filter-row--disabled" : ""}">
        <input type="checkbox"
               class="period-filter-checkbox"
               data-index="${index}"
               data-action="change->period-filter#onWeekToggle"
               ${checked}
               ${disabled}>
        <span class="period-filter-label">${escapeHtml(week.label)}</span>
        <span class="period-filter-day-range">${escapeHtml(week.day_range)}</span>
      </label>
    `
  }

  onYearCollapseToggle(event) {
    event.preventDefault()
    event.stopPropagation()

    const yearIndex = event.currentTarget.dataset.yearIndex
    const content = this.periodsContainerTarget.querySelector(`[data-year-content="${yearIndex}"]`)
    const chevron = event.currentTarget.querySelector(".period-filter-year-chevron")

    this.toggleCollapsible(content, chevron)
  }

  onMonthCollapseToggle(event) {
    event.preventDefault()
    event.stopPropagation()

    const monthIndex = event.currentTarget.dataset.monthIndex
    const content = this.periodsContainerTarget.querySelector(`[data-month-content="${monthIndex}"]`)
    const chevron = event.currentTarget.querySelector(".period-filter-month-chevron")

    this.toggleCollapsible(content, chevron)
  }

  toggleCollapsible(content, chevron) {
    this.toggleChevronRotation(chevron)

    if (content.classList.contains("period-filter-collapsible--collapsed")) {
      content.style.maxHeight = content.scrollHeight + "px"
      content.classList.remove("period-filter-collapsible--collapsed")
      content.addEventListener("transitionend", () => { content.style.maxHeight = "" }, { once: true })
    } else {
      content.style.maxHeight = content.scrollHeight + "px"
      requestAnimationFrame(() => {
        content.style.maxHeight = "0px"
        content.classList.add("period-filter-collapsible--collapsed")
      })
    }
  }

  selectionBounds() {
    if (this.selectedIndices.size === 0) return null

    return {
      min: Math.min(...this.selectedIndices),
      max: Math.max(...this.selectedIndices)
    }
  }

  isDisabled(index) {
    const bounds = this.selectionBounds()
    if (!bounds) return false

    return index > bounds.min && index < bounds.max
  }


  isRangeFullySelected(range) {
    for (let i = range.start; i <= range.end; i++) {
      if (!this.selectedIndices.has(i)) return false
    }
    return true
  }

  isRangePartiallySelected(range) {
    for (let i = range.start; i <= range.end; i++) {
      if (this.selectedIndices.has(i)) return true
    }
    return false
  }

  onWeekToggle(event) {
    event.stopPropagation()

    const index = parseInt(event.target.dataset.index)

    if (event.target.checked) {
      this.addToSelection(index)
    } else {
      this.removeFromSelection(index)
    }

    this.afterSelectionChange()
  }

  onYearToggle(event) {
    event.stopPropagation()

    const yearIndex = parseInt(event.target.dataset.yearIndex)
    const range = this.yearRanges[yearIndex]

    if (event.target.checked) {
      this.addRangeToSelection(range)
    } else {
      this.removeRangeFromSelection(range)
    }

    this.afterSelectionChange()
  }

  onMonthToggle(event) {
    event.stopPropagation()

    const monthIndex = parseInt(event.target.dataset.monthIndex)
    const range = this.monthRanges[monthIndex]

    if (event.target.checked) {
      this.addRangeToSelection(range)
    } else {
      this.removeRangeFromSelection(range)
    }

    this.afterSelectionChange()
  }

  addToSelection(index) {
    if (this.selectedIndices.size === 0) {
      this.selectedIndices.add(index)
      return
    }

    const { min, max } = this.selectionBounds()
    const newMin = Math.min(min, index)
    const newMax = Math.max(max, index)

    for (let i = newMin; i <= newMax; i++) {
      this.selectedIndices.add(i)
    }
  }

  addRangeToSelection(range) {
    if (this.selectedIndices.size === 0) {
      for (let i = range.start; i <= range.end; i++) {
        this.selectedIndices.add(i)
      }
      return
    }

    const { min, max } = this.selectionBounds()
    const newMin = Math.min(min, range.start)
    const newMax = Math.max(max, range.end)

    for (let i = newMin; i <= newMax; i++) {
      this.selectedIndices.add(i)
    }
  }

  removeFromSelection(index) {
    const { min, max } = this.selectionBounds()

    if (index === min || index === max) {
      this.selectedIndices.delete(index)
    }
  }

  removeRangeFromSelection(range) {
    const { min, max } = this.selectionBounds()

    if (range.start <= min && range.end >= min) {
      for (let i = range.start; i <= Math.min(range.end, max); i++) {
        this.selectedIndices.delete(i)
      }
    } else if (range.end >= max && range.start <= max) {
      for (let i = Math.max(range.start, min); i <= range.end; i++) {
        this.selectedIndices.delete(i)
      }
    }
  }

  afterSelectionChange() {
    if (this.selectedIndices.size === 0) {
      this.selectThisWeek()
      this.dispatchFilterChange()
      return
    }

    this.isThisWeekMode = false
    this.rerenderCheckboxes()
    this.updateFooter()
    this.updateHiddenInput()
    this.updateClearButtonVisibility()
    this.updateButtonStates(false)
    this.dispatchFilterChange()
  }

  rerenderCheckboxes() {
    const bounds = this.selectionBounds()
    this.renderWeekCheckboxes(bounds)
    this.renderMonthCheckboxes(bounds)
    this.renderYearCheckboxes(bounds)
  }

  renderWeekCheckboxes(bounds) {
    this.periodsContainerTarget.querySelectorAll(".period-filter-row").forEach(row => {
      const checkbox = row.querySelector(".period-filter-checkbox")
      const index = parseInt(checkbox.dataset.index)
      const isSelected = this.selectedIndices.has(index)
      const isMiddle = bounds && index > bounds.min && index < bounds.max

      checkbox.checked = isSelected
      checkbox.disabled = isMiddle
      row.classList.toggle("period-filter-row--selected", isSelected)
      row.classList.toggle("period-filter-row--disabled", isMiddle)
    })
  }

  renderMonthCheckboxes(bounds) {
    this.periodsContainerTarget.querySelectorAll(".period-filter-month-checkbox").forEach(checkbox => {
      const range = this.monthRanges[parseInt(checkbox.dataset.monthIndex)]
      const isMiddle = bounds && range.start > bounds.min && range.end < bounds.max
      checkbox.checked = this.isRangeFullySelected(range)
      checkbox.indeterminate = !checkbox.checked && this.isRangePartiallySelected(range)
      checkbox.disabled = isMiddle
      checkbox.closest(".period-filter-month-header").classList.toggle("period-filter-row--disabled", isMiddle)
    })
  }

  renderYearCheckboxes(bounds) {
    this.periodsContainerTarget.querySelectorAll(".period-filter-year-checkbox").forEach(checkbox => {
      const range = this.yearRanges[parseInt(checkbox.dataset.yearIndex)]
      const isMiddle = bounds && range.start > bounds.min && range.end < bounds.max
      checkbox.checked = this.isRangeFullySelected(range)
      checkbox.indeterminate = !checkbox.checked && this.isRangePartiallySelected(range)
      checkbox.disabled = isMiddle
      checkbox.closest(".period-filter-year-header").classList.toggle("period-filter-row--disabled", isMiddle)
    })
  }

  updateFooter() {
    if (this.selectedIndices.size === 0) {
      this.summaryTarget.textContent = ""
      this.countTarget.textContent = ""
      return
    }

    const { min, max } = this.selectionBounds()
    const olderDate = formatIsoToDisplay(this.flatWeeks[max].start_date)
    const newerDate = formatIsoToDisplay(this.flatWeeks[min].end_date)
    this.summaryTarget.textContent = `${olderDate} – ${newerDate}`

    const count = this.selectedIndices.size
    this.countTarget.textContent = `${count} week${count !== 1 ? "s" : ""} selected`
  }

  updateHiddenInput() {
    if (this.selectedIndices.size === 0) {
      this.inputTarget.value = ""
      return
    }

    const { min, max } = this.selectionBounds()
    const olderDate = formatIsoToForm(this.flatWeeks[max].start_date)
    const newerDate = formatIsoToForm(this.flatWeeks[min].end_date)

    this.inputTarget.value = `${olderDate} - ${newerDate}`
  }

  onSelectThisWeek(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isThisWeekMode) return

    this.close()
    this.selectThisWeek()
    this.dispatchFilterChange()
  }

  selectThisWeek() {
    this.isThisWeekMode = true
    this.selectedIndices = new Set()
    this.rerenderCheckboxes()
    this.updateFooter()
    this.setInputToCurrentWeek()
    this.updateClearButtonVisibility()
    this.updateButtonStates(true)
  }

  setInputToCurrentWeek() {
    this.inputTarget.value = this.currentWeekValue()
  }

  currentWeekValue() {
    const currentWeek = this.flatWeeks[0]
    if (!currentWeek) return ""

    const startDate = formatIsoToForm(currentWeek.start_date)
    const endDate = formatIsoToForm(currentWeek.end_date)
    return `${startDate} - ${endDate}`
  }

  isCurrentWeekValue(value) {
    return value === this.currentWeekValue()
  }

  updateButtonStates(isThisWeek) {
    this.thisWeekButtonTarget.classList.toggle("period-filter-btn--active", isThisWeek)
    this.periodButtonTarget.classList.toggle("period-filter-btn--active", !isThisWeek)
  }

  onClear(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.selectThisWeek()
    this.dispatchFilterChange()
  }

  handleFilterClear() {
    this.selectThisWeek()

    setTimeout(() => {
      this.dispatchInitializedEvent()
    }, 0)
  }

  updateClearButtonVisibility() {
    this.clearButtonTarget.style.display = this.selectedIndices.size === 0 ? "none" : ""
  }

  dispatchFilterChange() {
    const event = new CustomEvent("filter:change", {
      detail: {
        label: this.currentLabel(),
        value: this.inputTarget.value,
        filterKey: this.filterKeyValue
      }
    })
    this.inputTarget.dispatchEvent(event)
  }

  dispatchInitializedEvent() {
    const event = new CustomEvent("filter:initialized", {
      detail: {
        label: this.currentLabel(),
        value: this.inputTarget.value,
        filterKey: this.filterKeyValue
      }
    })

    this.inputTarget.dispatchEvent(event)
  }

  currentLabel() {
    return this.inputTarget.value || ""
  }

  restoreSelectionFromInput() {
    const value = this.inputTarget.value
    if (!value) return

    const [startStr, endStr] = value.split(" - ")
    if (!startStr || !endStr) return

    const startIso = parseFormDateToIso(startStr)
    const endIso = parseFormDateToIso(endStr)

    const startIndex = this.flatWeeks.findIndex(p => p.start_date === startIso)
    const endIndex = this.flatWeeks.findIndex(p => p.end_date === endIso)

    if (startIndex === -1 || endIndex === -1) return

    const min = Math.min(startIndex, endIndex)
    const max = Math.max(startIndex, endIndex)

    for (let i = min; i <= max; i++) {
      this.selectedIndices.add(i)
    }

    this.rerenderCheckboxes()
    this.updateFooter()
    this.updateClearButtonVisibility()
  }

  toggleChevronRotation(chevron) {
    const isRotated = chevron.style.transform === "rotate(180deg)"
    chevron.style.transform = isRotated ? "rotate(0deg)" : "rotate(180deg)"
  }

  resetChevronRotation(chevron) {
    chevron.style.transform = "rotate(0deg)"
  }

}
